import { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Avatar,
  Zoom,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const SparksLogo = "/images/sparks.webp";

// Fixed menu options — no free text input, no calls to any AI API.
const MENU_KEYS = ["services", "pricing", "timeline", "about", "contact"];

const Chatbot = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showContactActions, setShowContactActions] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content: t("chatbot.welcomeMessage"),
        },
      ]);
    }
  }, [isOpen, messages.length, t]);

  const handleNavigate = useCallback((path) => {
    setIsOpen(false);
    navigate(path);
  }, [navigate]);

  const handleEmailClick = useCallback(() => {
    const subject = encodeURIComponent(t("chatbot.emailSubject"));
    const body = encodeURIComponent(t("chatbot.emailBody"));
    window.location.href = `mailto:itenginesparks@gmail.com?subject=${subject}&body=${body}`;
  }, [t]);

  const handleWhatsAppClick = useCallback(() => {
    const message = encodeURIComponent(t("whatsapp.defaultMessage"));
    window.open(`https://wa.me/526611301597?text=${message}`, "_blank", "noopener,noreferrer");
  }, [t]);

  const handleMenuOptionClick = useCallback((key) => {
    setMessages((prev) => [
      ...prev,
      { role: "user", content: t(`chatbot.menu.${key}.label`) },
      { role: "assistant", content: t(`chatbot.menu.${key}.response`) },
    ]);

    if (key === "contact") {
      setShowContactActions(true);
    }
  }, [t]);

  return (
    <>
      {/* Floating Chat Button */}
      <Zoom in={!isOpen}>
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={() => setIsOpen(true)}
            aria-label={t("chatbot.openChat")}
            sx={{
              width: 60,
              height: 60,
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: "white",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              "&:hover": {
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                transform: "rotate(5deg)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <img
              src={SparksLogo}
              alt="Sparks AI Assistant"
              style={{ width: 36, height: 36, objectFit: "contain" }}
            />
          </IconButton>
        </motion.div>
      </Zoom>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              bottom: "24px",
              right: "24px",
              zIndex: 1001,
              width: "380px",
              maxWidth: "calc(100vw - 48px)",
              height: "600px",
              maxHeight: "calc(100vh - 48px)",
            }}
            role="dialog"
            aria-labelledby="chatbot-title"
            aria-modal="true"
          >
            <Paper
              elevation={8}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 3,
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(30, 30, 30, 0.95)"
                    : "rgba(255, 255, 255, 0.95)",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <img
                    src={SparksLogo}
                    alt="Sparks"
                    style={{ width: 40, height: 40, objectFit: "contain" }}
                  />
                  <Box>
                    <Typography id="chatbot-title" variant="h6" fontWeight="bold">
                      Sparks
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t("chatbot.subtitle")}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setIsOpen(false)}
                  aria-label={t("chatbot.closeChat")}
                  sx={{ color: "white" }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  "&::-webkit-scrollbar": { width: "8px" },
                  "&::-webkit-scrollbar-thumb": {
                    background: (theme) => theme.palette.primary.main,
                    borderRadius: "4px",
                  },
                }}
                role="log"
                aria-live="polite"
                aria-atomic="false"
              >
                {messages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        flexDirection: message.role === "user" ? "row-reverse" : "row",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: message.role === "user" ? "secondary.main" : "transparent",
                        }}
                        aria-hidden="true"
                      >
                        {message.role === "user" ? (
                          <PersonIcon fontSize="small" />
                        ) : (
                          <img
                            src={SparksLogo}
                            alt="Sparks"
                            style={{ width: 28, height: 28, objectFit: "contain" }}
                          />
                        )}
                      </Avatar>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          maxWidth: "75%",
                          bgcolor: message.role === "user" ? "primary.main" : "background.paper",
                          color: message.role === "user" ? "white" : "text.primary",
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                          {message.content}
                        </Typography>
                      </Paper>
                    </Box>
                  </motion.div>
                ))}

                {/* Contact Action Buttons */}
                {showContactActions && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Box sx={{ px: 1, mt: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        {t("chatbot.contactMethods")}
                      </Typography>
                      <Stack direction="column" spacing={1}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<EmailIcon />}
                          onClick={handleEmailClick}
                          sx={{
                            bgcolor: "#EA4335",
                            "&:hover": { bgcolor: "#D33426" },
                            justifyContent: "flex-start",
                            textTransform: "none",
                          }}
                        >
                          {t("chatbot.sendEmail")}
                        </Button>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          onClick={handleWhatsAppClick}
                          sx={{
                            bgcolor: "#25D366",
                            "&:hover": { bgcolor: "#128C7E" },
                            justifyContent: "flex-start",
                            textTransform: "none",
                          }}
                        >
                          {t("chatbot.sendWhatsApp")}
                        </Button>
                      </Stack>
                    </Box>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Guided menu — fixed options only, no free text input */}
              <Box
                sx={{
                  p: 2,
                  borderTop: 1,
                  borderColor: "divider",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(40, 40, 40, 0.8)"
                      : "rgba(245, 245, 245, 0.8)",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  {t("chatbot.quickQuestions")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
                  {MENU_KEYS.map((key) => (
                    <Chip
                      key={key}
                      label={t(`chatbot.menu.${key}.label`)}
                      size="small"
                      onClick={() => handleMenuOptionClick(key)}
                      sx={{
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: "primary.main",
                          color: "white",
                          transform: "translateY(-2px)",
                        },
                      }}
                    />
                  ))}
                </Stack>
                <Stack direction="column" spacing={1}>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleNavigate("/services")}
                    sx={{ justifyContent: "space-between" }}
                  >
                    {t("chatbot.viewServices")}
                  </Button>
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleNavigate("/contact")}
                    sx={{ justifyContent: "space-between" }}
                  >
                    {t("chatbot.goToContact")}
                  </Button>
                </Stack>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;
